import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  planCardBadgeRecipe,
  planCardBodyRecipe,
  planCardChevronRecipe,
  planCardCornerRecipe,
  planCardHeadRecipe,
  planCardRecipe,
  planCardStaticRecipe,
  type PlanCardRecipeProps,
} from '../recipes/plan-card.recipe';
import { Icon } from './Icon';
import { Divider, Inline, Stack } from './Layout';
import { List, ListItem } from './List';
import { Text } from './Text';

export interface PlanCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    Omit<PlanCardRecipeProps, 'emphasis'> {
  title: React.ReactNode;
  /**
   * The price line, laid out on one baseline: a figure, a struck-through
   * list price, "$0 for 3 months" beside "$49 / month after". Whatever the
   * plan's pricing is, it is the page's to spell out.
   */
  price?: React.ReactNode;
  description?: React.ReactNode;
  /** What is included, one line each, with a tick. */
  features?: React.ReactNode[];
  featuresTitle?: React.ReactNode;
  /** The button that picks the plan. Sits inside the folding body. */
  action?: React.ReactNode;
  /** A tag on the top edge — "Recommended". */
  badge?: React.ReactNode;
  /** The top-right corner — a countdown. */
  corner?: React.ReactNode;
  /** The primary border, for the plan being recommended. */
  emphasis?: boolean;
  /**
   * Folded shut behind a toggle. Give `onToggle` and the head becomes the
   * button that opens it; leave it out and the card is always open.
   */
  expanded?: boolean;
  onToggle?: () => void;
}

/**
 * A subscription plan, as a card.
 *
 *   <PlanCard
 *     size="lg"
 *     emphasis
 *     corner={<Countdown />}
 *     title="Essential"
 *     price={<Text variant="h1" as="span">$49</Text>}
 *     description="For a yard with one or two trucks."
 *     features={['Unlimited drivers', 'Priority support']}
 *   />
 *
 * With `onToggle`, the head is a button and the body slides open beneath it —
 * the "all plans" list, where every plan shows its name and price and only
 * the one you open shows the rest.
 */
export const PlanCard = React.forwardRef<HTMLDivElement, PlanCardProps>(
  function PlanCard(
    {
      title,
      price,
      description,
      features,
      featuresTitle = "What's included:",
      action,
      badge,
      corner,
      emphasis = false,
      size = 'md',
      expanded = false,
      onToggle,
      className,
      ...props
    },
    ref
  ) {
    const titleBlock = (
      <Stack gap={1}>
        <Text variant={size === 'lg' ? 'h3' : 'h4'}>{title}</Text>
        {price ? (
          <Inline gap={2} align="baseline" className="w-full">
            {price}
          </Inline>
        ) : null}
        {description && !onToggle ? (
          <Text variant="body-sm" tone="muted" className="mt-1">
            {description}
          </Text>
        ) : null}
      </Stack>
    );

    const featuresBlock = features?.length ? (
      <Stack gap={3}>
        <Text variant="body-sm-strong">{featuresTitle}</Text>
        <List marker="check" tone="muted" gap={2}>
          {features.map((feature, index) => (
            <ListItem key={index}>{feature}</ListItem>
          ))}
        </List>
      </Stack>
    ) : null;

    return (
      <div
        ref={ref}
        className={cn(planCardRecipe({ emphasis, size }), className)}
        {...props}
      >
        {badge ? <span className={planCardBadgeRecipe()}>{badge}</span> : null}
        {corner ? (
          <span className={planCardCornerRecipe()}>{corner}</span>
        ) : null}
        {onToggle ? (
          <>
            <button
              type="button"
              aria-expanded={expanded}
              onClick={onToggle}
              className={planCardHeadRecipe({ size })}
            >
              {titleBlock}
              <Icon
                icon={ChevronDown}
                size="md"
                className={planCardChevronRecipe({ expanded })}
              />
            </button>
            <div className={planCardBodyRecipe({ expanded })}>
              <Stack gap={2} className="px-5 pb-5">
                <Divider />
                <Stack gap={3}>
                  {description ? (
                    <Text variant="body-sm" tone="muted">
                      {description}
                    </Text>
                  ) : null}
                  {action}
                  {featuresBlock}
                </Stack>
              </Stack>
            </div>
          </>
        ) : (
          <div className={planCardStaticRecipe({ size })}>
            {titleBlock}
            {featuresBlock ? <Divider /> : null}
            {featuresBlock}
            {action}
          </div>
        )}
      </div>
    );
  }
);
